import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  AlertTitle,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const FeedbackContext = createContext(null);

const TOAST_AUTO_HIDE_MS = 6000;
const MAX_STACK = 5;

/** @typedef {'error' | 'warning' | 'info' | 'success'} NoticeSeverity */

/**
 * @typedef {object} PageNoticeInput
 * @property {string} [message]
 * @property {React.ReactNode} [content]
 * @property {NoticeSeverity} [severity]
 * @property {boolean} [persist]
 * @property {string} [title]
 */

function nextNoticeId() {
  return `notice-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return ctx;
}

/**
 * Register a page-scoped notice while `notice` is set; clears on unmount.
 * @param {string} id Stable id per component instance
 * @param {PageNoticeInput | null | undefined | false} notice
 */
export function usePageNotice(id, notice) {
  const { registerPageNotice, clearPageNotice } = useFeedback();
  const message = notice?.message ?? '';
  const severity = notice?.severity ?? 'info';
  const title = notice?.title ?? '';
  const persist = notice?.persist;

  useEffect(() => {
    if (!notice) {
      clearPageNotice(id);
      return () => clearPageNotice(id);
    }
    registerPageNotice(id, notice);
    return () => clearPageNotice(id);
  }, [id, notice, message, severity, title, persist, registerPageNotice, clearPageNotice]);
}

function NoticeStack({ notices, onDismiss, topOffsetPx }) {
  if (!notices.length) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: topOffsetPx,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        px: 2,
        pointerEvents: 'none',
      }}
    >
      {notices.map((entry) => (
        <Alert
          key={entry.key}
          severity={entry.severity}
          variant="filled"
          onClose={() => onDismiss(entry.key)}
          sx={{
            width: '100%',
            maxWidth: 560,
            pointerEvents: 'auto',
            boxShadow: 3,
          }}
        >
          {entry.title ? <AlertTitle>{entry.title}</AlertTitle> : null}
          {entry.content ?? entry.message}
        </Alert>
      ))}
    </Box>
  );
}

export function FeedbackProvider({ children }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState({});
  const confirmResolverRef = useRef(null);

  const [toastNotices, setToastNotices] = useState([]);
  const [pageNotices, setPageNotices] = useState({});
  const toastTimersRef = useRef({});

  const [noticeTopOffsetPx, setNoticeTopOffsetPx] = useState(8);

  useEffect(() => {
    const readOffset = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--notice-top-offset');
      const parsed = parseInt(raw, 10);
      setNoticeTopOffsetPx(Number.isFinite(parsed) ? parsed : 8);
    };
    readOffset();
    window.addEventListener('notice-top-offset-change', readOffset);
    return () => window.removeEventListener('notice-top-offset-change', readOffset);
  }, []);

  const dismissNotice = useCallback((key) => {
    setToastNotices((prev) => prev.filter((n) => n.key !== key));
    if (toastTimersRef.current[key]) {
      clearTimeout(toastTimersRef.current[key]);
      delete toastTimersRef.current[key];
    }
  }, []);

  const pushToast = useCallback((message, severity = 'error', options = {}) => {
    const key = nextNoticeId();
    const entry = {
      key,
      message: String(message || ''),
      severity,
      title: options.title,
      content: options.content,
      persist: false,
    };
    setToastNotices((prev) => [...prev, entry].slice(-MAX_STACK));
    toastTimersRef.current[key] = setTimeout(() => {
      dismissNotice(key);
    }, options.autoHideDuration ?? TOAST_AUTO_HIDE_MS);
  }, [dismissNotice]);

  const showAlert = useCallback((message, severity = 'error', options) => {
    pushToast(message, severity, options);
  }, [pushToast]);

  const showError = useCallback(
    (message, options) => showAlert(message, 'error', options),
    [showAlert],
  );

  const showSuccess = useCallback(
    (message, options) => showAlert(message, 'success', options),
    [showAlert],
  );

  const registerPageNotice = useCallback((id, notice) => {
    setPageNotices((prev) => ({
      ...prev,
      [id]: {
        key: id,
        message: notice.message != null ? String(notice.message) : '',
        content: notice.content,
        severity: notice.severity || 'info',
        title: notice.title,
        persist: notice.persist !== false,
      },
    }));
  }, []);

  const clearPageNotice = useCallback((id) => {
    setPageNotices((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmOptions({
        title: opts.title ?? t('common.confirmTitle'),
        message: opts.message ?? '',
        confirmLabel: opts.confirmLabel ?? t('common.confirm'),
        cancelLabel: opts.cancelLabel ?? t('common.cancel'),
        confirmColor: opts.confirmColor ?? 'primary',
      });
      setConfirmOpen(true);
    });
  }, [t]);

  const closeConfirm = useCallback((result) => {
    setConfirmOpen(false);
    if (confirmResolverRef.current) {
      confirmResolverRef.current(result);
      confirmResolverRef.current = null;
    }
  }, []);

  const stackedNotices = useMemo(() => {
    const pageList = Object.values(pageNotices);
    const combined = [...pageList, ...toastNotices];
    return combined.slice(-MAX_STACK);
  }, [pageNotices, toastNotices]);

  const handleDismiss = useCallback((key) => {
    if (pageNotices[key]) {
      clearPageNotice(key);
      return;
    }
    dismissNotice(key);
  }, [pageNotices, clearPageNotice, dismissNotice]);

  const value = useMemo(() => ({
    confirm,
    showAlert,
    showError,
    showSuccess,
    registerPageNotice,
    clearPageNotice,
  }), [confirm, showAlert, showError, showSuccess, registerPageNotice, clearPageNotice]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <NoticeStack
        notices={stackedNotices}
        onDismiss={handleDismiss}
        topOffsetPx={noticeTopOffsetPx}
      />
      <Dialog
        open={confirmOpen}
        onClose={() => closeConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        {confirmOptions.title && (
          <DialogTitle>{confirmOptions.title}</DialogTitle>
        )}
        <DialogContent>
          <DialogContentText component="div">
            {confirmOptions.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => closeConfirm(false)}>
            {confirmOptions.cancelLabel}
          </Button>
          <Button
            onClick={() => closeConfirm(true)}
            variant="contained"
            color={confirmOptions.confirmColor}
            autoFocus
          >
            {confirmOptions.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
}
